import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { getExpiryStatus, getExpiryBadge } from "@/lib/signatureBookExpiry";

interface SignatureBook {
  id: string;
  title: string;
  status: string;
  event_date: string | null;
  guest_book_enabled: boolean;
  closing_message: string | null;
  activated_at: string | null;
  expires_at: string | null;
  client_id: string | null;
  contract_id: string | null;
  job_id: string | null;
  public_slug: string | null;
}

interface Client   { id: string; full_name: string; email: string; }
interface Job      { id: string; title: string; }
interface Contract { id: string; title?: string; }

interface Props {
  book: SignatureBook;
  tenantId: string;
  onChange: <K extends keyof SignatureBook>(k: K, v: SignatureBook[K]) => void;
  onSave: () => void;
  saving: boolean;
}

const STATUS_OPTIONS = [
  { value: "draft",     label: "Borrador",   color: "text-slate-500" },
  { value: "published", label: "Publicado",  color: "text-emerald-600" },
  { value: "archived",  label: "Archivado",  color: "text-orange-500" },
];

export default function OverviewTab({ book, tenantId, onChange, onSave, saving }: Props) {
  const [clients,   setClients]   = useState<Client[]>([]);
  const [jobs,      setJobs]      = useState<Job[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);

  useEffect(() => {
    const load = async () => {
      const [cr, jr, co] = await Promise.all([
        supabase.from("clients").select("id, full_name, email").eq("tenant_id", tenantId).eq("status", "active").order("full_name"),
        supabase.from("jobs").select("id, title").eq("tenant_id", tenantId).order("title"),
        supabase.from("contracts").select("id, title").eq("tenant_id", tenantId).order("created_at", { ascending: false }),
      ]);
      if (cr.data)  setClients(cr.data as Client[]);
      if (jr.data)  setJobs(jr.data as Job[]);
      if (co.data)  setContracts(co.data as Contract[]);
    };
    load();
  }, [tenantId]);

  const inp = "w-full border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-300";
  const lbl = "block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide";

  return (
    <div className="space-y-6 max-w-2xl">

      {/* Title + status */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={lbl}>Título *</label>
          <input type="text" value={book.title} onChange={(e) => onChange("title", e.target.value)} className={inp} placeholder="Ej: Boda García & López" />
        </div>
        <div>
          <label className={lbl}>Estado</label>
          <select value={book.status} onChange={(e) => onChange("status", e.target.value)} className={inp}>
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Client */}
      <div>
        <label className={lbl}>Cliente *</label>
        <select value={book.client_id ?? ""} onChange={(e) => onChange("client_id", e.target.value || null)} className={inp}>
          <option value="">Selecciona cliente...</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>{c.full_name} — {c.email}</option>
          ))}
        </select>
      </div>

      {/* Contract + Job */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={lbl}>Contrato</label>
          <select value={book.contract_id ?? ""} onChange={(e) => onChange("contract_id", e.target.value || null)} className={inp}>
            <option value="">— Sin contrato —</option>
            {contracts.map((c) => (
              <option key={c.id} value={c.id}>{c.title || c.id.slice(0, 12)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={lbl}>Job / Sesión</label>
          <select value={book.job_id ?? ""} onChange={(e) => onChange("job_id", e.target.value || null)} className={inp}>
            <option value="">— Sin job —</option>
            {jobs.map((j) => (
              <option key={j.id} value={j.id}>{j.title}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Event date */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={lbl}>Fecha del Evento</label>
          <input type="date" value={book.event_date ?? ""} onChange={(e) => onChange("event_date", e.target.value || null)} className={inp} />
        </div>
        <div>
          <label className={lbl}>Slug público</label>
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-xs shrink-0">/guestbook/</span>
            <input type="text" value={book.public_slug ?? ""} readOnly className={`${inp} bg-slate-50 text-slate-400 cursor-default`} />
          </div>
        </div>
      </div>

      {/* Activation dates */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={lbl}>Activado el</label>
          <input
            type="datetime-local"
            value={book.activated_at ? book.activated_at.slice(0, 16) : ""}
            onChange={(e) => onChange("activated_at", e.target.value ? new Date(e.target.value).toISOString() : null)}
            className={inp}
          />
        </div>
        <div>
          <label className={lbl}>Expira el</label>
          <input
            type="datetime-local"
            value={book.expires_at ? book.expires_at.slice(0, 16) : ""}
            onChange={(e) => onChange("expires_at", e.target.value ? new Date(e.target.value).toISOString() : null)}
            className={inp}
          />
        </div>
      </div>

      {/* Guest book toggle */}
      <div className="flex items-center justify-between p-4 rounded-xl bg-amber-50 border border-amber-100">
        <div>
          <p className="text-sm font-semibold text-slate-700">Libro de Visitas Activo</p>
          <p className="text-xs text-slate-400 mt-0.5">Permite que los invitados dejen mensajes sobre las fotos</p>
        </div>
        <button
          type="button"
          onClick={() => onChange("guest_book_enabled", !book.guest_book_enabled)}
          className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${book.guest_book_enabled ? "bg-amber-500" : "bg-slate-200"}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${book.guest_book_enabled ? "translate-x-5" : "translate-x-0"}`} />
        </button>
      </div>

      {/* Expiry info panel */}
      {book.expires_at && (() => {
        const expStatus = getExpiryStatus(book.expires_at);
        const badge     = getExpiryBadge(expStatus);
        const exp       = new Date(book.expires_at);
        const now       = new Date();
        const daysLeft  = Math.ceil((exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        return (
          <div className={`flex items-start gap-3 p-4 rounded-xl border ${
            expStatus === "expired" ? "bg-red-50 border-red-200" :
            expStatus === "soon"    ? "bg-amber-50 border-amber-200" :
                                     "bg-emerald-50 border-emerald-200"
          }`}>
            <span className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${badge.className}`}>
              <i className={`${badge.icon} text-base`}></i>
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-slate-700">Vigencia del libro</p>
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${badge.className}`}>
                  {badge.label}
                </span>
              </div>
              {expStatus === "expired" ? (
                <p className="text-xs text-red-500 mt-0.5">
                  Venció el {exp.toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" })}.
                  Los invitados ya no pueden dejar mensajes.
                </p>
              ) : (
                <p className="text-xs text-slate-500 mt-0.5">
                  Vence el <strong>{exp.toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" })}</strong>
                  {daysLeft > 0 && ` · faltan ${daysLeft} día${daysLeft === 1 ? "" : "s"}`}.
                </p>
              )}
            </div>
          </div>
        );
      })()}

      {/* Closing message */}
      <div>
        <label className={lbl}>Mensaje de cierre</label>
        <textarea
          rows={3}
          maxLength={500}
          value={book.closing_message ?? ""}
          onChange={(e) => onChange("closing_message", e.target.value || null)}
          placeholder="Mensaje que los invitados ven al final del libro..."
          className={`${inp} resize-none`}
        />
        <p className="text-xs text-slate-400 mt-1">{(book.closing_message ?? "").length}/500</p>
      </div>

      <div className="pt-2">
        <button
          onClick={onSave}
          disabled={saving}
          className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-sm font-semibold px-6 py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
        >
          {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <i className="ri-save-line"></i>}
          {saving ? "Guardando…" : "Guardar cambios"}
        </button>
      </div>
    </div>
  );
}